import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";

import ClubbieModal from "../../../../components/Modal/ClubbieModal";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";

const INPUT_CLASS =
  "w-full h-10 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors";

const LABEL_CLASS = "block text-[14px] font-normal text-clubbie-white";

const ExtraPointsModal = ({
  isOpen,
  group,
  getTeamById,
  formData,
  setFormData,
  onSubmit,
  onClose,
  isLoading,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  // Reset dropdown when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsDropdownOpen(false);
      setSearch("");
    }
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const teams = (group?.teams || []).filter((t) => t.teamId);
  const filtered = teams.filter((t) => {
    const name = getTeamById(t.teamId) || "";
    return name.toLowerCase().includes(search.toLowerCase());
  });
  const selectedTeamName = formData.teamId ? (getTeamById(formData.teamId) || "Unknown Team") : "";

  const handleSelectTeam = (team) => {
    setFormData({
      teamId: team.teamId,
      extraPoints: team.extraPoints || 0,
    });
    setIsDropdownOpen(false);
    setSearch("");
  };

  const footer = (
    <div className="flex justify-end gap-3 w-full">
      <button
        type="button"
        className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
        onClick={onClose}
      >
        Cancel
      </button>
      <button
        type="button"
        className="bg-[#00E7FF] hover:opacity-50 text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        onClick={onSubmit}
        disabled={isLoading || !formData.teamId}
      >
        {isLoading ? <><LoadingSpinner size="small" /><span>Saving...</span></> : "Save"}
      </button>
    </div>
  );

  return (
    <ClubbieModal
      isOpen={isOpen}
      width="450px"
      toggle={onClose}
      title={`Extra Points — ${group?.name || "Group"}`}
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        {/* Team Selection */}
        <div className="space-y-1">
          <label className={LABEL_CLASS}>Select team</label>
          <div className="relative" ref={dropdownRef}>
            <div
              className={`flex items-center justify-between h-11 px-4 bg-[#021529] border rounded-full cursor-pointer transition-colors ${isDropdownOpen ? "border-[#00E7FF]" : "border-transparent"}`}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span className={`text-[15px] ${formData.teamId ? "text-[#FEFCEB]" : "text-[#7F8585]"}`}>
                {selectedTeamName || "Select a team"}
              </span>
              <svg className={`w-4 h-4 text-[#FEFCEB] transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            {isDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-app-bg border border-[#192634] rounded-[20px] shadow-lg overflow-hidden z-[1000]">
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    <Search size={16} />
                  </div>
                  <input
                    type="text"
                    className="w-full h-10 bg-transparent text-[#FEFCEB] text-sm outline-none placeholder-[#7F8585] pl-9 pr-4"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="max-h-[200px] overflow-y-auto border-t border-[#192634]">
                  {filtered.length > 0 ? (
                    filtered.map((team) => (
                      <div
                        key={team.teamId}
                        className={`flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors border-b border-[#192634] last:border-b-0 hover:bg-app-hover ${
                          formData.teamId === team.teamId ? "bg-app-hover" : ""
                        }`}
                        onClick={() => handleSelectTeam(team)}
                      >
                        <span className={`text-sm font-medium ${formData.teamId === team.teamId ? "text-[#00E7FF]" : "text-[#FEFCEB]"}`}>
                          {getTeamById(team.teamId) || "Unknown Team"}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-3 text-[#7F8585] text-sm">No teams found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Extra Points Input */}
        <div className="space-y-1">
          <label className={LABEL_CLASS}>Extra points</label>
          <input
            type="number"
            className={INPUT_CLASS}
            value={formData.extraPoints}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                extraPoints: e.target.value,
              }))
            }
            placeholder="0"
            onWheel={(e) => e.target.blur()}
          />
          <p className="text-xs text-app-text-secondary mt-1">
            Extra points will be added to the team's total points in standings.
          </p>
        </div>
      </div>
    </ClubbieModal>
  );
};

export default ExtraPointsModal;
